/**
 * MessageList 组件
 * 负责渲染消息列表，处理无限滚动、加载更多消息和滚动位置记忆
 */
import { useRef, useEffect, useCallback, memo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { throttle } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import {
  currentMessagesAtom,
  hasMoreMessagesAtom,
  loadMoreMessagesAtom,
  selectedConversationIdAtom,
} from '../../../../atoms/v3/chatAtoms';
import { shouldAutoScrollAtom } from '../../../../atoms/v3/scrollControlAtoms';
import { updateTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { globalTaskEditAtom } from '../../../../atoms/v3/taskAtoms';
import ChatMessage from './ChatMessage';


const MessageListComponent: React.FC = () => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messages = useAtomValue(currentMessagesAtom);
  const hasMoreMessages = useAtomValue(hasMoreMessagesAtom);
  const loadMoreMessages = useSetAtom(loadMoreMessagesAtom);
  // 移除滚动位置状态，不再需要保存滚动位置
  const conversationId = useAtomValue(selectedConversationIdAtom);
  const [shouldAutoScroll, setShouldAutoScroll] = useAtom(shouldAutoScrollAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, setGlobalTaskEdit] = useAtom(globalTaskEditAtom);

  // 创建基础滚动函数 - 优化版，确保立即执行
  const baseScrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (!messagesEndRef.current) {
      console.warn('messagesEndRef.current is null, cannot scroll');
      return;
    }

    // 使用requestAnimationFrame确保在下一帧立即执行滚动
    // 这可以避免多个滚动请求堆积导致的延迟
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior, block: 'start' });
        // console.log('Programmatic scroll to bottom with behavior:', behavior);
      }
    });
  }, []);

  // 创建节流版本的滚动函数 - 使用Lodash的throttle
  // 对于smooth滚动使用300ms的间隔，对于auto滚动使用100ms的间隔
  const throttledScrollToBottomSmooth = useCallback(
    throttle(
      () => baseScrollToBottom('smooth'),
      300,
      { leading: true, trailing: true }
    ),
    [baseScrollToBottom]
  );

  const throttledScrollToBottomAuto = useCallback(
    throttle(
      () => baseScrollToBottom('auto'),
      50, // 减少到50ms，使自动滚动更加快速响应
      { leading: true, trailing: true }
    ),
    [baseScrollToBottom]
  );

  // 对外暴露的滚动函数 - 根据behavior选择合适的节流版本
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (behavior === 'smooth') {
      throttledScrollToBottomSmooth();
    } else {
      throttledScrollToBottomAuto();
    }
  }, [throttledScrollToBottomSmooth, throttledScrollToBottomAuto]);

  // 不再需要暴露滚动方法给父组件，所有滚动控制通过 shouldAutoScrollAtom 完成

  // 移除消息展开/折叠处理逻辑，现在由 ChatMessage 组件内部处理

  // 处理任务状态切换
  const handleToggleTask = useCallback(async (taskId: string, completed: boolean) => {
    await updateTask({
      id: taskId,
      done: completed,
    });
  }, [updateTask]);

  // 处理任务点击 - 用于 MessageTaskReferences 组件中的任务项点击
  const handleTaskClick = useCallback((taskId: string) => {
    // 这个函数专门处理 MessageTaskReferences 中的任务项点击
    // tempo:// 协议链接由 TempoProtocolLink 组件自己处理
    setGlobalTaskEdit({
      taskId,
      isOpen: true,
      triggerSource: 'hyperlink'
    });
  }, [setGlobalTaskEdit]);

  // 移除滚动位置保存逻辑，提高渲染性能

  // 用户交互处理 - 一旦检测到用户交互，立即禁用自动滚动
  const handleUserInteraction = useCallback(() => {
    // 只有当shouldAutoScroll为true时才需要处理
    if (shouldAutoScroll) {
      console.log('User interaction detected, disabling auto-scroll');

      // 立即设置为false，阻止任何正在进行的或将要进行的自动滚动
      setShouldAutoScroll(false);
    }
  }, [shouldAutoScroll, setShouldAutoScroll]);

  // 用户交互事件监听（pointerdown 和 wheel）
  useEffect(() => {
    const messageList = messageListRef.current;
    const documentElement = document;

    // 创建一个文档级别的wheel事件处理函数，以便能正确移除
    const documentWheelHandler = (e: WheelEvent) => {
      // 检查事件是否发生在消息列表内
      if (messageListRef.current && messageListRef.current.contains(e.target as Node)) {
        // console.log('Document wheel event detected, disabling auto-scroll');
        handleUserInteraction();
      }
    };

    if (messageList) {
      // 监听指针按下事件（触摸屏和鼠标）
      messageList.addEventListener('pointerdown', handleUserInteraction, { capture: true });

      // 监听滚轮事件（鼠标滚轮和触摸板）- 在捕获阶段，优先级最高
      messageList.addEventListener('wheel', handleUserInteraction, { capture: true, passive: false });

      // 在文档级别监听滚轮事件，确保捕获所有滚动，包括触摸板
      documentElement.addEventListener('wheel', documentWheelHandler, { capture: true, passive: true });

      // 监听触摸板特定事件（如果有的话）
      messageList.addEventListener('touchmove', handleUserInteraction, { passive: true });

      // 移除滚动事件监听，只依赖wheel和pointerdown事件
    }

    return () => {
      if (messageList) {
        messageList.removeEventListener('pointerdown', handleUserInteraction, { capture: true });
        messageList.removeEventListener('wheel', handleUserInteraction, { capture: true });
        messageList.removeEventListener('touchmove', handleUserInteraction);
        // 不再需要移除滚动事件监听器
      }

      // 正确移除文档级别的事件监听器
      documentElement.removeEventListener('wheel', documentWheelHandler, { capture: true });
    };
  }, [handleUserInteraction, shouldAutoScroll]);

  // 移除滚动位置保存相关的事件监听器

  // 加载更多消息
  const handleLoadMore = useCallback(() => {
    if (!conversationId) return;
    if (hasMoreMessages) {
      loadMoreMessages();
    }
  }, [conversationId, hasMoreMessages, loadMoreMessages]);

  // 滚动监听以自动加载更多
  useEffect(() => {
    const handleScroll = () => {
      if (!messageListRef.current || !conversationId) return;
      const { scrollTop } = messageListRef.current;
      if (scrollTop < 50 && hasMoreMessages) {
        handleLoadMore();
      }
    };
    const messageList = messageListRef.current;
    if (messageList) messageList.addEventListener('scroll', handleScroll);
    return () => {
      if (messageList) messageList.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, conversationId, handleLoadMore]);

  // 统一的滚动逻辑 - 处理所有需要滚动的情况
  useEffect(() => {
    // 如果不应该自动滚动，直接返回
    if (!shouldAutoScroll) return;

    // 如果没有会话ID或消息为空，直接返回
    if (!conversationId || messages.length === 0) return;

    // 使用极短的延迟，给用户机会在滚动开始前进行交互
    // 同时避免多个滚动请求同时执行
    const timer = setTimeout(() => {
      // 再次检查shouldAutoScroll，如果用户在延迟期间进行了交互，就不会滚动
      if (shouldAutoScroll) {
        // console.log('Auto-scrolling to bottom');
        // 执行滚动
        scrollToBottom('auto');
      } else {
        // console.log('Auto-scroll cancelled by user interaction');
      }
    }, 20); // 使用20毫秒的极短延迟，几乎立即响应但仍能捕获用户交互

    return () => clearTimeout(timer); // 清理定时器
  }, [conversationId, messages, shouldAutoScroll, scrollToBottom]);

  // 清理节流函数
  useEffect(() => {
    return () => {
      // 取消节流函数，避免内存泄漏
      throttledScrollToBottomSmooth.cancel();
      throttledScrollToBottomAuto.cancel();
    };
  }, [throttledScrollToBottomSmooth, throttledScrollToBottomAuto]);



  return (
    <div
      ref={messageListRef}
      className="flex-1 overflow-y-auto px-4 pt-[calc(4.5rem+env(safe-area-inset-top))] pb-[calc(15rem+env(safe-area-inset-bottom))] relative z-10"
    >
      {hasMoreMessages && (
        <div className="text-center mb-4">
          <button
            onClick={handleLoadMore}
            className="px-3 py-1 text-xs font-medium rounded-full text-ios-blue dark:text-ios-dark-blue bg-ios-gray6 dark:bg-ios-dark-gray5 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray4 transition-colors"
          >
            {t('v3/chat:actions.loadMore')}
          </button>
        </div>
      )}

      <AnimatePresence initial={false}>
        {messages.map(message => (
          <ChatMessage
            key={message.id}
            message={message}
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
          />
        ))}
      </AnimatePresence>

      {/* 添加一个空的div作为滚动目标，确保在ChatInputContainer上方有足够空间 */}
      <div ref={messagesEndRef} style={{ height: '1px', marginBottom: '1rem' }} />
    </div>
  );
};

export const MessageList = memo(MessageListComponent);
export default MessageList;
